import React, { useState, useEffect } from "react";
import ReactECharts from "echarts-for-react";
import { useSelector } from "react-redux";
import Cookies from "js-cookie";
import { FaChartBar, FaChartLine, FaChartPie } from "react-icons/fa";
import { useTheme } from "../../Themecontext/Themecontext";

const Chart = ({ data = [], title = "", noWrapper = false }) => {
    // ================= MOBILE =================

    const { Colourdata } = useSelector((state) => state.ClientLogin);

    const iscolour = Colourdata?.data?.find((el) => el.ExeName === "GRAYNEW");

    const iscolourDark = Colourdata?.data?.find(
        (el) => el.ExeName === "GRAYDARKNEW",
    );

    const { theme } = useTheme();
    const isdark = theme;

    const [isMobile, setIsMobile] = useState(window.innerWidth < 576);

    const textColor = isdark === "light" ? "#1f2937" : "#ffffff";

    const splitLineColor = isdark === "light"
        ? (iscolour?.Colour4 || "rgba(0, 0, 0, 0.08)")
        : (iscolourDark?.Colour6 || "rgba(255, 255, 255, 0.08)");

    const getLabelInterval = () => {
        const totalLabels = chartLabels?.length || 0;

        // desktop pe sab dikhao
        if (!isMobile) return 0;

        // mobile responsive logic
        if (totalLabels > 25) {
            return (index) => index % 3 !== 0; // 1 show, 2 skip
        }

        if (totalLabels > 12) {
            return (index) => index % 2 !== 0; // alternate
        }

        return 0; // sab show
    };

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 576);
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const formatToTwoDecimal = (val) => {
        const num = Number(val);

        if (isNaN(num)) return "0.00";

        return num.toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    // ================= CHART TYPE =================
    const [chartType, setChartType] = useState("bar");

    // ================= DATA =================
    const hiddenColumns = [
        "inwMeter",
        "inwOpMeter",
        "TotalOut",
        "NewBal",
        "Shortage",
        "FinMtr",
        "TotalOpInwMeter",
        "FinishMeter",
        "BalMeter",
        "TotalInwMeter",
        "MonthName",
        "readings",
        "ProductionGroupMstName",
        "AcName",
        "ItemName"
    ];

    const filteredChartData =
        data?.filter((item) => {
            const columnName = item?.keyName;

            return !hiddenColumns.includes(columnName);
        }) || [];

    const chartLabels = filteredChartData?.map(
        (item) =>
            item?.month === null ||
                item?.month === undefined ||
                item?.month === ""
                ? "N/A"
                : String(item?.month)
    );

    const chartValues = filteredChartData?.map((item) => {
        const num = Number(item?.value || 0);

        return isNaN(num) ? 0 : num;
    });

    const gradientColor =
        isdark === "light"
            ? iscolour?.Colour1
            : iscolourDark?.Colour1;

    const maxPieItems = 12;

    const isPieLimitExceeded =
        filteredChartData.length > maxPieItems;

    const limitedPieData =
        filteredChartData.slice(0, maxPieItems);

    // ================= LINE OPTION =================
    const lineOption = {
        tooltip: {
            trigger: "axis",
            confine: true,
            formatter: function (params) {
                const data = params[0];

                return `
            <div>
                <strong>${data.axisValue}</strong><br/>
                Value: ${formatToTwoDecimal(data.value)}
            </div>
        `;
            },
        },

        grid: {
            left: "5%",
            right: "5%",
            top: "15%",
            containLabel: true,
        },

        xAxis: {
            type: "category",
            boundaryGap: false,
            data: chartLabels,

            axisLabel: {
                interval: isMobile && chartLabels?.length > 20 ? 1 : 0,
                color: textColor,
                fontSize: isMobile ? 9 : 12,
                hideOverlap: true,

                formatter: (value) => {
                    if (!value) return "N/A";

                    return String(value).length > 10
                        ? String(value).slice(0, 10) + "..."
                        : value;
                },
            },
        },

        yAxis: {
            type: "value",

            axisLabel: {
                fontSize: isMobile ? 10 : 12,
                color: textColor,
            },

            splitLine: {
                show: true,

                lineStyle: {
                    color: splitLineColor,
                    type: "solid",
                    width: 1,
                },
            },
        },

        series: [
            {
                name: "India",
                type: "line",
                smooth: true,
                data: chartValues,

                lineStyle: {
                    width: isMobile ? 2 : 4,
                    color:
                        isdark === "light" ? iscolour?.Colour1 : iscolourDark?.Colour1,
                },

                itemStyle: {
                    color:
                        isdark === "light" ? iscolour?.Colour1 : iscolourDark?.Colour1,
                },

                areaStyle: {
                    color: {
                        type: "linear",

                        x: 0,
                        y: 0,

                        x2: 0,
                        y2: 1,

                        colorStops: [
                            {
                                offset: 0,
                                color: gradientColor + "CC", // 80% opacity
                            },

                            {
                                offset: 0.5,
                                color: gradientColor + "66", // 40% opacity
                            },

                            {
                                offset: 1,
                                color: gradientColor + "05", // almost transparent
                            },
                        ],
                    },
                },
            },
        ],
    };

    const barOption = {
        tooltip: {
            trigger: "axis",
            confine: true,

            textStyle: {
                fontSize: isMobile ? 10 : 12,
            },

            padding: isMobile ? 6 : 10,

            formatter: function (params) {
                const data = params[0];

                return `
            <div>
                <strong>${data.axisValue}</strong><br/>
                Value: ${formatToTwoDecimal(data.value)}
            </div>
        `;
            },
        },

        grid: {
            left: "8%",
            right: "3%",
            top: "10%",
            bottom: "8%",
            containLabel: true,
        },
        xAxis: {
            type: "category",
            data: chartLabels || [],

            boundaryGap: true,

            axisTick: {
                alignWithLabel: true,
            },

            splitLine: {
                show: false,
            },

            axisLabel: {
                interval: getLabelInterval(), // same as line chart
                color: textColor,

                rotate: isMobile ? 60 : 45,
                fontSize: isMobile ? 9 : 12,

                hideOverlap: true,
                overflow: "truncate",

                formatter: (value) => {
                    if (!value) return "N/A";

                    return String(value).length > 10
                        ? String(value).slice(0, 10) + "..."
                        : value;
                },
            },
        },

        yAxis: {
            type: "value",

            axisLabel: {
                fontSize: isMobile ? 10 : 12,
                color: textColor,
            },

            splitLine: {
                show: true,
                lineStyle: {
                    color: splitLineColor,
                    type: "solid",
                    width: 1,
                },
            },
        },

        series: [
            {
                type: "bar",

                data: chartValues || [],

                barWidth: isMobile ? "65%" : "70%",

                itemStyle: {
                    borderRadius: [8, 8, 0, 0],

                    color: {
                        type: "linear",
                        x: 0,
                        y: 0,
                        x2: 0,
                        y2: 1,

                        colorStops: [
                            {
                                offset: 0,
                                color:
                                    isdark === "light"
                                        ? iscolour?.Colour1
                                        : iscolourDark?.Colour1,
                            },
                        ],
                    },
                },
            },
        ],
    };
    // ================= PIE OPTION =================
    const pieOption = {
        tooltip: {
            trigger: "item",
            confine: true,

            textStyle: {
                fontSize: isMobile ? 10 : 12,
            },

            padding: isMobile ? 6 : 10,
            borderWidth: 0,

            formatter: function (params) {
                return `
            <div>
                <strong>${params.name}</strong><br/>
                Value: ${formatToTwoDecimal(params.value)}<br/>
                ${params.percent}%
            </div>
        `;
            },
        },

        legend: {
            top: isMobile ? "78%" : "85%",

            itemWidth: isMobile ? 10 : 18,
            itemHeight: isMobile ? 10 : 18,

            textStyle: {
                fontSize: isMobile ? 8 : 12,
                color: textColor,
            },
        },

        series: [
            {
                name: "Share",
                type: "pie",

                top: isMobile ? 10 : 20,
                bottom: isMobile ? 20 : 40,

                radius: isMobile ? ["40%", "60%"] : ["50%", "75%"],

                center: isMobile ? ["50%", "36%"] : ["50%", "42%"],

                avoidLabelOverlap: true,

                data:
                    limitedPieData
                        ?.map((item) => ({
                            name: item?.month || "",
                            value: Number(item?.value || 0),
                        })) || [],
                label: {
                    show: !isMobile,
                    position: "outside",
                    formatter: "{b}: {d}%",
                    fontSize: 12,
                    color: textColor,
                },

                labelLine: {
                    show: !isMobile,
                    length: isMobile ? 8 : 15,
                    length2: isMobile ? 5 : 10,

                    lineStyle: {
                        width: 2,

                        color: textColor,
                    },
                },

                color: [
                    isdark === "light" ? iscolour?.Colour1 : iscolourDark?.Colour1,

                    isdark === "light" ? iscolour?.Colour2 : iscolourDark?.Colour2,

                    isdark === "light" ? iscolour?.Colour3 : iscolourDark?.Colour3,

                    isdark === "light" ? iscolour?.Colour4 : iscolourDark?.Colour4,

                    isdark === "light" ? iscolour?.Colour5 : iscolourDark?.Colour5,

                    isdark === "light" ? iscolour?.Colour6 : iscolourDark?.Colour6,

                    isdark === "light" ? iscolour?.Colour7 : iscolourDark?.Colour7,

                    isdark === "light" ? iscolour?.Colour8 : iscolourDark?.Colour8,

                    isdark === "light" ? iscolour?.Colour9 : iscolourDark?.Colour9,

                    isdark === "light" ? iscolour?.Colour10 : iscolourDark?.Colour10,

                    isdark === "light" ? iscolour?.Colour13 : iscolourDark?.Colour13,

                    isdark === "light" ? iscolour?.Colour14 : iscolourDark?.Colour14,
                ],

                itemStyle: {
                    borderColor: "#fff",
                    borderWidth: 2,
                    borderRadius: 8,
                },

                emphasis: {
                    scale: true,

                    itemStyle: {
                        shadowBlur: 10,
                        shadowColor: "rgba(14, 14, 14, 0.25)",
                    },
                },
            },
        ],
    };

    // ================= GET OPTION =================
    const getOption = () => {
        switch (chartType) {
            case "line":
                return lineOption;

            case "pie":
                return pieOption;

            case "bar":
                return barOption;

            default:
                return barOption;
        }
    };

    const hasChartData =
        chartValues &&
        chartValues.length > 0 &&
        chartValues.some((val) => Number(val) > 0);

    return (
        <div
            style={
                noWrapper
                    ? {
                        width: "100%",
                        paddingTop: isMobile ? "10px" : "15px",
                        paddingBottom: isMobile ? "10px" : "15px",
                        boxSizing: "border-box"
                    }
                    : {
                        width: "100%",
                        backgroundColor: isdark === "light" ? "#ffffff" : iscolourDark?.Colour7 || "#23262f",
                        borderRadius: "16px",
                        boxShadow: isdark === "light" ? "0 10px 30px rgba(0,0,0,0.06)" : "0 10px 30px rgba(0,0,0,0.3)",
                        border: isdark === "light" ? "1px solid rgba(0, 0, 0, 0.08)" : `1px solid ${iscolourDark?.Colour6 || "rgba(255, 255, 255, 0.08)"}`,
                        padding: isMobile ? "15px" : "24px",
                        marginBottom: "20px",
                        boxSizing: "border-box"
                    }
            }
        >
            {noWrapper ? (
                /* Only buttons on the right */
                <div
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: "10px",
                        flexWrap: "wrap",
                        paddingBottom: "15px",
                        paddingRight: "20px"
                    }}
                >
                    <button
                        onClick={() => setChartType("bar")}
                        title="Bar Chart"
                        className="hover-scale-btn"
                        style={{
                            width: isMobile ? "30px" : "40px",
                            height: isMobile ? "30px" : "40px",
                            border: "none",
                            borderRadius: "12px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "0.3s",
                            backgroundColor:
                                chartType === "bar"
                                    ? isdark === "light"
                                        ? iscolour?.Colour1
                                        : iscolourDark?.Colour5
                                    : "transparent",
                            color:
                                chartType === "bar"
                                    ? isdark === "light"
                                        ? iscolour?.Colour5
                                        : iscolourDark?.Colour6
                                    : isdark === "light"
                                        ? iscolour?.Colour3
                                        : iscolourDark?.Colour4,
                            border: `2px solid ${chartType === "bar"
                                ? isdark === "light"
                                    ? iscolour?.Colour1
                                    : iscolourDark?.Colour5
                                : isdark === "light"
                                    ? iscolour?.Colour4
                                    : iscolourDark?.Colour5
                                }`,
                        }}
                    >
                        <FaChartBar size={isMobile ? 16 : 20} />
                    </button>

                    <button
                        onClick={() => setChartType("line")}
                        title="Line Chart"
                        className="hover-scale-btn"
                        style={{
                            width: isMobile ? "30px" : "40px",
                            height: isMobile ? "30px" : "40px",
                            border: "none",
                            borderRadius: "12px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "0.3s",
                            backgroundColor:
                                chartType === "line"
                                    ? isdark === "light"
                                        ? iscolour?.Colour1
                                        : iscolourDark?.Colour5
                                    : "transparent",
                            color:
                                chartType === "line"
                                    ? isdark === "light"
                                        ? iscolour?.Colour5
                                        : iscolourDark?.Colour6
                                    : isdark === "light"
                                        ? iscolour?.Colour3
                                        : iscolourDark?.Colour4,
                            border: `2px solid ${chartType === "line"
                                ? isdark === "light"
                                    ? iscolour?.Colour1
                                    : iscolourDark?.Colour5
                                : isdark === "light"
                                    ? iscolour?.Colour4
                                    : iscolourDark?.Colour5
                                }`,
                        }}
                    >
                        <FaChartLine size={isMobile ? 16 : 20} />
                    </button>

                    <button
                        onClick={() => setChartType("pie")}
                        title="Pie Chart"
                        className="hover-scale-btn"
                        style={{
                            width: isMobile ? "30px" : "40px",
                            height: isMobile ? "30px" : "40px",
                            border: "none",
                            borderRadius: "12px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "0.3s",
                            backgroundColor:
                                chartType === "pie"
                                    ? isdark === "light"
                                        ? iscolour?.Colour1
                                        : iscolourDark?.Colour5
                                    : "transparent",
                            color:
                                chartType === "pie"
                                    ? isdark === "light"
                                        ? iscolour?.Colour5
                                        : iscolourDark?.Colour6
                                    : isdark === "light"
                                        ? iscolour?.Colour3
                                        : iscolourDark?.Colour4,
                            border: `2px solid ${chartType === "pie"
                                ? isdark === "light"
                                    ? iscolour?.Colour1
                                    : iscolourDark?.Colour5
                                : isdark === "light"
                                    ? iscolour?.Colour4
                                    : iscolourDark?.Colour5
                                }`,
                        }}
                    >
                        <FaChartPie size={isMobile ? 16 : 20} />
                    </button>
                </div>
            ) : (
                /* Header with Title and Buttons */
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        borderBottom: isdark === "light" ? "1px solid rgba(0, 0, 0, 0.08)" : `1px solid ${iscolourDark?.Colour6 || "rgba(255, 255, 255, 0.08)"}`,
                        paddingBottom: "15px",
                        marginBottom: "20px",
                        flexWrap: "wrap",
                        gap: "10px"
                    }}
                >
                    <div>
                        <h3
                            style={{
                                margin: 0,
                                fontSize: isMobile ? "15px" : "18px",
                                fontWeight: "700",
                                color: isdark === "light" ? "#1f2937" : "#ffffff",
                                letterSpacing: "0.5px",
                                textTransform: "uppercase"
                            }}
                        >
                            {title || "Chart Analysis"}
                        </h3>
                    </div>

                    {/* BUTTONS */}
                    <div
                        style={{
                            display: "flex",
                            gap: "8px",
                        }}
                    >
                        <button
                            onClick={() => setChartType("bar")}
                            title="Bar Chart"
                            className="hover-scale-btn"
                            style={{
                                width: isMobile ? "30px" : "40px",
                                height: isMobile ? "30px" : "40px",
                                border: "none",
                                borderRadius: "12px",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                transition: "0.3s",
                                backgroundColor:
                                    chartType === "bar"
                                        ? isdark === "light"
                                            ? iscolour?.Colour1
                                            : iscolourDark?.Colour5
                                        : "transparent",
                                color:
                                    chartType === "bar"
                                        ? isdark === "light"
                                            ? iscolour?.Colour5
                                            : iscolourDark?.Colour6
                                        : isdark === "light"
                                            ? iscolour?.Colour3
                                            : iscolourDark?.Colour4,
                                border: `2px solid ${chartType === "bar"
                                    ? isdark === "light"
                                        ? iscolour?.Colour1
                                        : iscolourDark?.Colour5
                                    : isdark === "light"
                                        ? iscolour?.Colour4
                                        : iscolourDark?.Colour5
                                    }`,
                            }}
                        >
                            <FaChartBar size={isMobile ? 16 : 20} />
                        </button>

                        <button
                            onClick={() => setChartType("line")}
                            title="Line Chart"
                            className="hover-scale-btn"
                            style={{
                                width: isMobile ? "30px" : "40px",
                                height: isMobile ? "30px" : "40px",
                                border: "none",
                                borderRadius: "12px",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                transition: "0.3s",
                                backgroundColor:
                                    chartType === "line"
                                        ? isdark === "light"
                                            ? iscolour?.Colour1
                                            : iscolourDark?.Colour5
                                        : "transparent",
                                color:
                                    chartType === "line"
                                        ? isdark === "light"
                                            ? iscolour?.Colour5
                                            : iscolourDark?.Colour6
                                        : isdark === "light"
                                            ? iscolour?.Colour3
                                            : iscolourDark?.Colour4,
                                border: `2px solid ${chartType === "line"
                                    ? isdark === "light"
                                        ? iscolour?.Colour1
                                        : iscolourDark?.Colour5
                                    : isdark === "light"
                                        ? iscolour?.Colour4
                                        : iscolourDark?.Colour5
                                    }`,
                            }}
                        >
                            <FaChartLine size={isMobile ? 16 : 20} />
                        </button>

                        <button
                            onClick={() => setChartType("pie")}
                            title="Pie Chart"
                            className="hover-scale-btn"
                            style={{
                                width: isMobile ? "30px" : "40px",
                                height: isMobile ? "30px" : "40px",
                                border: "none",
                                borderRadius: "12px",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                transition: "0.3s",
                                backgroundColor:
                                    chartType === "pie"
                                        ? isdark === "light"
                                            ? iscolour?.Colour1
                                            : iscolourDark?.Colour5
                                        : "transparent",
                                color:
                                    chartType === "pie"
                                        ? isdark === "light"
                                            ? iscolour?.Colour5
                                            : iscolourDark?.Colour6
                                        : isdark === "light"
                                            ? iscolour?.Colour3
                                            : iscolourDark?.Colour4,
                                border: `2px solid ${chartType === "pie"
                                    ? isdark === "light"
                                        ? iscolour?.Colour1
                                        : iscolourDark?.Colour5
                                    : isdark === "light"
                                        ? iscolour?.Colour4
                                        : iscolourDark?.Colour5
                                    }`,
                            }}
                        >
                            <FaChartPie size={isMobile ? 16 : 20} />
                        </button>
                    </div>
                </div>
            )}

            {/* CHART */}
            <div
                style={{
                    width: "100%",
                    height: isMobile ? "350px" : "400px",
                }}
            >
                {!hasChartData ? (
                    <div
                        style={{
                            position: "relative",
                            width: "100%",
                            height: "100%",
                            minHeight: "350px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                            borderRadius: "20px",
                            background: noWrapper
                                ? (isdark === "light" ? iscolour?.Colour5 : iscolourDark?.Colour7)
                                : "rgba(0, 0, 0, 0.02)",
                            padding: "20px",
                        }}
                    >
                        {/* BACKGROUND CIRCLE */}
                        <div
                            style={{
                                position: "absolute",
                                width: "220px",
                                height: "220px",
                                borderRadius: "50%",
                                background: noWrapper
                                    ? (isdark === "light" ? "rgba(0,0,0,0.03)" : "rgba(255,255,255,0.03)")
                                    : "rgba(0, 0, 0, 0.03)",
                                top: "-70px",
                                right: "-70px",
                            }}
                        />

                        <div
                            style={{
                                position: "absolute",
                                width: "160px",
                                height: "160px",
                                borderRadius: "50%",
                                background: noWrapper
                                    ? (isdark === "light" ? "rgba(0,0,0,0.03)" : "rgba(255,255,255,0.03)")
                                    : "rgba(0, 0, 0, 0.03)",
                                bottom: "-50px",
                                left: "-50px",
                            }}
                        />

                        {/* CARD */}
                        <div
                            style={{
                                position: "relative",
                                width: "100%",
                                maxWidth: "450px",
                                padding: "35px 25px",
                                borderRadius: "24px",
                                textAlign: "center",
                                background: noWrapper
                                    ? (isdark === "light" ? "rgba(255,255,255,0.75)" : "rgba(35,38,47,0.75)")
                                    : "#ffffff",
                                backdropFilter: "blur(10px)",
                                boxShadow: noWrapper
                                    ? (isdark === "light" ? "0 10px 35px rgba(0,0,0,0.08)" : "0 10px 35px rgba(0,0,0,0.35)")
                                    : "0 10px 35px rgba(0,0,0,0.05)",
                                border: noWrapper
                                    ? (isdark === "light" ? "1px solid rgba(255,255,255,0.7)" : "1px solid rgba(255,255,255,0.08)")
                                    : "1px solid rgba(0,0,0,0.08)",
                            }}
                        >
                            {/* ICON */}
                            <div
                                style={{
                                    width: "90px",
                                    height: "90px",
                                    borderRadius: "50%",
                                    margin: "0 auto 20px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    background: noWrapper
                                        ? (isdark === "light" ? "linear-gradient(135deg,#f3f7ff,#dce8ff)" : "linear-gradient(135deg,#2d3342,#1f2430)")
                                        : "linear-gradient(135deg,#f3f7ff,#dce8ff)",
                                }}
                            >
                                <FaChartPie
                                    size={40}
                                    color={
                                        noWrapper
                                            ? (isdark === "light" ? iscolour?.Colour1 : iscolourDark?.Colour1)
                                            : (iscolour?.Colour1 || "#1f2937")
                                    }
                                />
                            </div>

                            {/* TITLE */}
                            <h3
                                style={{
                                    fontWeight: "700",
                                    fontSize: "24px",
                                    marginBottom: "10px",
                                    color: noWrapper
                                        ? (isdark === "light" ? iscolour?.Colour3 : iscolourDark?.Colour4)
                                        : "#1f2937",
                                }}
                            >
                                No Chart Data Available
                            </h3>

                            {/* SUBTITLE */}
                            <p
                                style={{
                                    margin: 0,
                                    fontSize: "15px",
                                    lineHeight: "24px",
                                    color: noWrapper
                                        ? (isdark === "light" ? "rgba(0,0,0,0.6)" : "rgba(255,255,255,0.6)")
                                        : "rgba(0,0,0,0.6)",
                                }}
                            >
                                There is currently no data available to display
                            </p>
                        </div>
                    </div>
                ) : chartType === "pie" && isPieLimitExceeded ? (
                    <div
                        style={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "18px",
                            fontWeight: "600",
                            color: "#dc3545",
                            textAlign: "center",
                        }}
                    >
                        Pie Chart supports maximum 12 columns only
                    </div>
                ) : (
                    <ReactECharts
                        key={chartType}
                        option={getOption()}
                        notMerge={true}
                        lazyUpdate={true}
                        autoResize={true}
                        opts={{
                            renderer: "canvas",
                        }}
                        style={{
                            width: "100%",
                            height: "100%",
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export default Chart;
